import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import {
  Plus,
  Search,
  Edit,
  Trash2,
  Eye,
  EyeOff,
  Image as ImageIcon,
  Clock,
  Heart,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { getImageUrl } from "@/utils/imageUrl";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import AdminLayout from "@/components/layout/AdminLayout";
import axios from "axios";
import { API } from "@/App";
import AdminPagination from "@/components/admin/AdminPagination";

const categoryLabels = {
  general: { label: "Historia", color: "bg-blue-100 text-blue-800" },
  "antes-despues": { label: "Antes/Después", color: "bg-purple-100 text-purple-800" },
  tradicion: { label: "Tradición", color: "bg-orange-100 text-orange-800" },
  personaje: { label: "Personaje", color: "bg-green-100 text-green-800" },
  oficio: { label: "Oficio", color: "bg-yellow-100 text-yellow-800" },
};

export default function AdminMemorias() {
  const [memories, setMemories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [deleteId, setDeleteId] = useState(null);
  const [filters, setFilters] = useState({
    search: "",
    category: "all",
    status: "all",
  });

  const [limit, setLimit] = useState(25);

  useEffect(() => {
    fetchMemories();
  }, [page, limit, filters]);

  const fetchMemories = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.search) params.append("search", filters.search);
      if (filters.category !== "all") params.append("category", filters.category);
      if (filters.status !== "all") params.append("status", filters.status);
      params.append("skip", (page - 1) * limit);
      params.append("limit", limit);

      const response = await axios.get(`${API}/memories?${params}`, {
      });
      setMemories(response.data.items);
      setTotal(response.data.total);
    } catch (error) {
      console.error("Error fetching memories:", error);
      toast.error("Error al cargar las memorias");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await axios.delete(`${API}/memories/${deleteId}`);
      toast.success("Memoria eliminada");
      fetchMemories();
    } catch (error) {
      toast.error("Error al eliminar");
    }
    setDeleteId(null);
  };


  return (
    <AdminLayout>
      <div className="space-y-6" data-testid="admin-memorias">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-brand-text">
              Memorias
            </h1>
            <p className="text-brand-textSecondary mt-1">
              {total} memorias en total
            </p>
          </div>
          <Link to="/admin/memorias/new">
            <Button className="rounded-full" data-testid="new-memory-btn">
              <Plus className="h-4 w-4 mr-2" />
              Nueva Memoria
            </Button>
          </Link>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-2xl p-4 shadow-card">
          <div className="flex flex-col lg:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <Input
                type="text"
                placeholder="Buscar memorias..."
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                className="pl-10"
              />
            </div>
            <Select
              value={filters.category}
              onValueChange={(value) => setFilters({ ...filters, category: value })}
            >
              <SelectTrigger className="w-full lg:w-48">
                <SelectValue placeholder="Categoría" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todas</SelectItem>
                <SelectItem value="general">Historia</SelectItem>
                <SelectItem value="antes-despues">Antes/Después</SelectItem>
                <SelectItem value="tradicion">Tradición</SelectItem>
                <SelectItem value="personaje">Personaje</SelectItem>
                <SelectItem value="oficio">Oficio</SelectItem>
              </SelectContent>
            </Select>
            <Select
              value={filters.status}
              onValueChange={(value) => setFilters({ ...filters, status: value })}
            >
              <SelectTrigger className="w-full lg:w-40">
                <SelectValue placeholder="Estado" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Todos</SelectItem>
                <SelectItem value="published">Publicado</SelectItem>
                <SelectItem value="draft">Borrador</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={() => fetchMemories()}>Filtrar</Button>
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-2xl shadow-card overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12"></TableHead>
                <TableHead>Título</TableHead>
                <TableHead>Categoría</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead>Votos</TableHead>
                <TableHead>Fecha</TableHead>
                <TableHead className="text-right">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell colSpan={7}>
                      <div className="h-12 bg-gray-100 animate-pulse rounded" />
                    </TableCell>
                  </TableRow>
                ))
              ) : memories.length > 0 ? (
                memories.map((memory) => (
                  <TableRow key={memory.memory_id}>
                    <TableCell>
                      {memory.featured_image_url ? (
                        <img
                          src={getImageUrl(memory.featured_image_url)}
                          alt=""
                          className="w-10 h-10 rounded object-cover"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded bg-gray-100 flex items-center justify-center">
                          <ImageIcon className="h-5 w-5 text-gray-400" />
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="font-medium text-brand-text line-clamp-1">
                        {memory.title}
                      </div>
                      {memory.year_approx && (
                        <div className="text-sm text-brand-textMuted flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {memory.year_approx}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <Badge className={categoryLabels[memory.category]?.color || "bg-gray-100"}>
                        {categoryLabels[memory.category]?.label || memory.category}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {memory.status === "published" ? (
                        <span className="flex items-center gap-1 text-green-600">
                          <Eye className="h-4 w-4" />
                          Publicado
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 text-yellow-600">
                          <EyeOff className="h-4 w-4" />
                          Borrador
                        </span>
                      )}
                    </TableCell>
                    <TableCell>
                      <span className="flex items-center gap-1 text-brand-textSecondary">
                        <Heart className="h-4 w-4" />
                        {memory.vote_count || 0}
                      </span>
                    </TableCell>
                    <TableCell className="text-brand-textSecondary">
                      {memory.created_at
                        ? new Date(memory.created_at).toLocaleDateString("es-HN")
                        : "-"}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Link to={`/admin/memorias/${memory.memory_id}`}>
                          <Button variant="ghost" size="icon">
                            <Edit className="h-4 w-4" />
                          </Button>
                        </Link>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => setDeleteId(memory.memory_id)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-12">
                    <p className="text-brand-textSecondary">No hay memorias</p>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>

        {/* Pagination */}
        <AdminPagination
          page={page}
          total={total}
          limit={limit}
          onPageChange={setPage}
          onLimitChange={(l) => { setLimit(l); setPage(1); }}
          testId="memorias-pagination"
          label="memorias"
        />

        {/* Delete Dialog */}
        <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>¿Eliminar esta memoria?</AlertDialogTitle>
              <AlertDialogDescription>
                Esta acción no se puede deshacer.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Cancelar</AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDelete}
                className="bg-red-600 hover:bg-red-700"
              >
                Eliminar
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </AdminLayout>
  );
}
